import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './auth/AuthContext.jsx';
import { ProtectedRoute } from './auth/ProtectedRoute.jsx';
import { Layout } from './components/Layout.jsx';
import { LoginPage } from './pages/LoginPage.jsx';
import { UnderwriterDashboard } from './pages/UnderwriterDashboard.jsx';
import { UnderwritingCanvas } from './pages/UnderwritingCanvas.jsx';
import { SubmissionDetail } from './pages/SubmissionDetail.jsx';
import { AccountPage } from './pages/AccountPage.jsx';
import { AccountList } from './pages/AccountList.jsx';
import { LeadershipDashboard } from './pages/LeadershipDashboard.jsx';
import { BrokerPortal } from './pages/BrokerPortal.jsx';
import { BrokerQuoteView } from './pages/BrokerQuoteView.jsx';
import { ProductManagement } from './pages/ProductManagement.jsx';
import { ProductDetail } from './pages/ProductDetail.jsx';
import { ApprovalStub } from './pages/ApprovalStub.jsx';
import { CapacityPartners } from './pages/CapacityPartners.jsx';
import { BrokerDashboard } from './pages/BrokerDashboard.jsx';
import { BrokerDetail } from './pages/BrokerDetail.jsx';
import { Documentation } from './pages/Documentation.jsx';
import { ApiDocs } from './pages/ApiDocs.jsx';
import { PolicyManagement } from './pages/PolicyManagement.jsx';
import { CommissionManagement } from './pages/CommissionManagement.jsx';
import { ComplianceFiling } from './pages/ComplianceFiling.jsx';
import { ReinsuranceTreaty } from './pages/ReinsuranceTreaty.jsx';
import { AuditTrail } from './pages/AuditTrail.jsx';

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        {/* Underwriter routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <UnderwriterDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/submissions/:id"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <SubmissionDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/underwriting/:submissionId"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <UnderwritingCanvas />
            </ProtectedRoute>
          }
        />

        {/* Account routes */}
        <Route
          path="/accounts"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <AccountList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts/:id"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <AccountPage />
            </ProtectedRoute>
          }
        />

        {/* Leadership routes */}
        <Route
          path="/leadership"
          element={
            <ProtectedRoute groups={['leadership']}>
              <LeadershipDashboard />
            </ProtectedRoute>
          }
        />

        {/* Capacity Partners */}
        <Route
          path="/capacity-partners"
          element={
            <ProtectedRoute groups={['leadership', 'capacity-provider']}>
              <CapacityPartners />
            </ProtectedRoute>
          }
        />

        {/* Broker CRM routes */}
        <Route
          path="/brokers"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <BrokerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/brokers/:id"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <BrokerDetail />
            </ProtectedRoute>
          }
        />

        {/* Product management */}
        <Route
          path="/products"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <ProductManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/products/:id"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <ProductDetail />
            </ProtectedRoute>
          }
        />

        {/* PAS routes */}
        <Route
          path="/policies"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <PolicyManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/commissions"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <CommissionManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/compliance"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <ComplianceFiling />
            </ProtectedRoute>
          }
        />
        <Route
          path="/treaties"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <ReinsuranceTreaty />
            </ProtectedRoute>
          }
        />

        {/* Step 3 stub */}
        <Route
          path="/approvals"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <ApprovalStub />
            </ProtectedRoute>
          }
        />

        {/* Documentation routes */}
        <Route
          path="/docs"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <Documentation />
            </ProtectedRoute>
          }
        />
        <Route
          path="/api-docs"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <ApiDocs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit-trail"
          element={
            <ProtectedRoute groups={['underwriter', 'leadership']}>
              <AuditTrail />
            </ProtectedRoute>
          }
        />

        {/* Broker routes */}
        <Route
          path="/broker"
          element={
            <ProtectedRoute groups={['broker']}>
              <BrokerPortal />
            </ProtectedRoute>
          }
        />
        <Route
          path="/broker/quotes/:id"
          element={
            <ProtectedRoute groups={['broker']}>
              <BrokerQuoteView />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default App;
